import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import { CalendarIcon, ChevronDown, RotateCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import DatePickerInput from '@/components/ui/DatePickerInput';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type DateRange = {
  from?: Date;
  to?: Date;
};

type DatePickerWithRangeProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> & {
  onChange?: (date: DateRange | undefined) => void;
  value?: DateRange;
  label?: string;
  startDateLabel?: string;
  strictRange?: boolean;
  endDateLabel?: string;
  defaultDateTimeRange?: DateRange;
  errorMessage?: string;
  isShowIcon?: boolean;
  isNoRangeDate?: boolean;
  isNoValidateFuture?: boolean;
  prefixText?: string;
  keepLabelWhenSelected?: boolean;
  isShowChevron?: boolean;
  classNameText?: string;
  classNamePlaceholder?: string;
  classNameIcon?: string;
  maxDateRange?: number;
  maxDateRangeLabel?: string;
  allowToDateWithoutFrom?: boolean;
};

type FieldError = {
  from: string | null;
  to: string | null;
};

const getCleanRange = (range?: DateRange): DateRange | undefined => {
  if (!range?.from && !range?.to) return undefined;
  return range;
};

export function DatePickerWithRange({
  className,
  onChange,
  value,
  label,
  startDateLabel,
  endDateLabel,
  strictRange,
  defaultDateTimeRange,
  errorMessage,
  isShowIcon = true,
  isShowChevron,
  prefixText,
  keepLabelWhenSelected,
  classNameText,
  classNamePlaceholder,
  classNameIcon,
  maxDateRange = 92,
  maxDateRangeLabel,
  isNoRangeDate,
  allowToDateWithoutFrom = true,
  isNoValidateFuture,
}: DatePickerWithRangeProps) {
  const { t } = useTranslation();
  const [date, setDate] = useState<DateRange | undefined>();
  const [internalErrorMessage, setInternalErrorMessage] = useState<FieldError>({
    from: null,
    to: null,
  });
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    setDate(value);
  }, [value]);

  const getRangeExceededMessage = () =>
    t('common.dateRangeExceeded', {
      defaultValue: `Date range cannot exceed ${
        maxDateRangeLabel ?? `${maxDateRange} days`
      }`,
      duration:
        maxDateRangeLabel ?? t('common.daysCount', { count: maxDateRange }),
    });

  const isFutureDate = (day: Date) =>
    !isNoValidateFuture && startOfDay(day) > startOfDay(new Date());

  const getDateRangeError = (
    range: DateRange | undefined,
  ): { field: 'from' | 'to'; message: string } | null => {
    if (!range) return null;

    if (range.from && isFutureDate(range.from)) {
      return {
        field: 'from',
        message: t('common.dateInFuture', 'Future dates are not allowed'),
      };
    }

    if (range.to && isFutureDate(range.to)) {
      return {
        field: 'to',
        message: t('common.dateInFuture', 'Future dates are not allowed'),
      };
    }

    if (
      range.from &&
      range.to &&
      startOfDay(range.to) < startOfDay(range.from)
    ) {
      return {
        field: 'to',
        message: t(
          'common.endDateMustBeAfterStartDate',
          'End date must be after start date',
        ),
      };
    }

    if (range.from && range.to && !isNoRangeDate && maxDateRange > 0) {
      const days = differenceInCalendarDays(
        startOfDay(range.to),
        startOfDay(range.from),
      );
      if (days > maxDateRange) {
        return { field: 'to', message: getRangeExceededMessage() };
      }
    }

    return null;
  };

  const getToDateMaxDate = () => {
    if (isNoValidateFuture) {
      if (isNoRangeDate || !date?.from || maxDateRange <= 0) return undefined;
      const maxDate = new Date(date.from);
      maxDate.setDate(maxDate.getDate() + maxDateRange);
      return maxDate;
    }

    if (isNoRangeDate || !date?.from || maxDateRange <= 0) return new Date();
    const maxDate = new Date(date.from);
    maxDate.setDate(maxDate.getDate() + maxDateRange);
    return maxDate < new Date() ? maxDate : new Date();
  };

  const handleDateChange = (
    newDate: DateRange | undefined,
    changedField?: 'from' | 'to',
  ) => {
    if (
      strictRange &&
      !allowToDateWithoutFrom &&
      !newDate?.from &&
      newDate?.to
    ) {
      return;
    }

    const nextError = getDateRangeError(newDate);
    if (nextError) {
      setInternalErrorMessage(prev =>
        changedField === 'from'
          ? {
              from: nextError.field === 'from' ? nextError.message : null,
              to: null,
            }
          : { ...prev, [nextError.field]: nextError.message },
      );
      return;
    }

    setInternalErrorMessage(prev =>
      !changedField || changedField === 'from'
        ? { from: null, to: null }
        : { ...prev, to: null },
    );
    const cleanRange = getCleanRange(newDate);
    setDate(cleanRange);
    onChange?.(cleanRange);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setInternalErrorMessage({ from: null, to: null });
      setResetKey(key => key + 1);
    }
  };

  const hasSelectedDate = !!(date?.from || date?.to);
  const shouldShowPrefix =
    hasSelectedDate && (prefixText || keepLabelWhenSelected);
  const displayLabel = label || t('common.dateRange', 'Select date range');
  const toDisabled = !isNoRangeDate && !allowToDateWithoutFrom && !date?.from;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className="w-full">
          <Button
            type="button"
            id="date"
            variant="outline"
            className={cn(
              'h-10 w-full justify-start gap-2 rounded-lg border-gray-200 bg-white px-3 py-2 text-left text-sm font-normal text-gray-900 hover:border-[#f06f24] hover:bg-white hover:text-gray-900',
              !hasSelectedDate && 'text-gray-500',
              errorMessage && 'border-[#ff4d4f]',
              className,
            )}
            classBtn="w-full justify-start gap-1"
          >
            {isShowIcon && (
              <CalendarIcon
                className={cn('h-4 w-4 shrink-0 text-gray-500', classNameIcon)}
              />
            )}
            <span className={cn('min-w-0 flex-1 truncate', classNameText)}>
              {shouldShowPrefix && <>{prefixText || displayLabel}: </>}
              {hasSelectedDate ? (
                <>
                  {date?.from && format(date.from, 'dd/MM/yyyy')}
                  {date?.from && date?.to && ' - '}
                  {date?.to && format(date.to, 'dd/MM/yyyy')}
                </>
              ) : (
                <span className={cn('text-gray-500', classNamePlaceholder)}>
                  {displayLabel}
                </span>
              )}
            </span>
            {isShowChevron && (
              <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-gray-500" />
            )}
          </Button>
          {errorMessage && (
            <p className="mt-1 text-xs leading-4 text-[#ff4d4f]">
              {errorMessage}
            </p>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[min(520px,calc(100vh-32px))] w-[min(432px,calc(100vw-32px))] overflow-auto rounded-xl p-4"
        align="start"
        collisionPadding={16}
        sideOffset={8}
      >
        <div className="mb-3 text-sm font-semibold text-gray-900">
          {displayLabel}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 sm:gap-x-4">
          <div className="flex min-w-0 flex-col gap-2">
            <span className="text-xs font-medium text-gray-500">
              {startDateLabel || 'Start date'}
            </span>
            <DatePickerInput
              key={`from-${resetKey}`}
              date={date?.from}
              setDate={val =>
                handleDateChange(
                  {
                    from: val instanceof Date ? val : undefined,
                    to: undefined,
                  },
                  'from',
                )
              }
              placeholder="dd/mm/yyyy"
              maxDate={isNoValidateFuture ? undefined : new Date()}
              hasError={!!internalErrorMessage.from}
              errorMessage={internalErrorMessage.from ?? undefined}
              classNameErrorMessage="static mt-0"
              isShowBtnFooter={false}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <span className="text-xs font-medium text-gray-500">
              {endDateLabel || 'End date'}
            </span>
            <DatePickerInput
              key={`to-${resetKey}-${date?.from?.getTime() ?? 'empty'}`}
              date={date?.to}
              setDate={val =>
                handleDateChange(
                  {
                    from: date?.from,
                    to: val instanceof Date ? val : undefined,
                  },
                  'to',
                )
              }
              placeholder="dd/mm/yyyy"
              minDate={date?.from}
              maxDate={getToDateMaxDate()}
              readOnly={toDisabled}
              hasError={!!internalErrorMessage.to}
              errorMessage={internalErrorMessage.to ?? undefined}
              className={cn(toDisabled && 'bg-gray-50 text-gray-400')}
              classNameErrorMessage="static mt-0"
              isShowBtnFooter={false}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            className="h-9 rounded-lg px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            onClick={() => {
              setResetKey(key => key + 1);
              setInternalErrorMessage({ from: null, to: null });
              handleDateChange(defaultDateTimeRange);
            }}
          >
            <span>{t('common.reset', 'Reset')}</span>
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
